<script setup lang="ts">
import { Combobox } from '@ark-ui/vue'
import UiIcon from '../UiIcon.vue'
import UiText from '../UiText.vue'
import UiLink from '../UiLink.vue'
import UiComboboxItem from '../UiComboboxItem.vue'
import UiComboboxGroup from '../UiComboboxGroup.vue'
import type { ComboboxGroupData, ComboboxItemData } from '../types/combobox'

interface Props {
    isFetchingValue: boolean
    hasResults: boolean
    isErrorValue: boolean
    isGrouped: boolean
    groupedData: ComboboxGroupData[]
    multiple: boolean
    getGroupItems: (groupId: string) => ComboboxItemData[]
    getLinkProps: (link: string | { href: string; label: string } | undefined) => { href: string | undefined; label: string | undefined }
    hideComboboxWhenEmpty?: boolean
    hasMore?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{ (e: 'load-more'): void }>()

const LOAD_MORE_THRESHOLD_PX = 120

function onScroll(event: Event) {
    if (!props.hasMore || props.isFetchingValue) return
    const el = event.currentTarget as HTMLElement
    if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX) emit('load-more')
}
</script>

<template>
    <Combobox.Content
        class="border border-se-gray-300 rounded-lg shadow-lg bg-se-white overflow-hidden z-100 max-h-[min(var(--available-height),500px)] overflow-y-auto"
        :class="{
            'hidden': props.hideComboboxWhenEmpty && !props.hasResults,
        }"
        @scroll.passive="onScroll"
    >
        <div v-if="props.isFetchingValue && !props.hasResults" class="px-4 py-8 text-center">
            <slot name="loading">
                <UiText as="p" variant="p" class="text-se-gray-500">Searching...</UiText>
            </slot>
        </div>

        <div v-else-if="isErrorValue" class="px-4 py-8 text-center">
            <UiIcon name="close" class="w-6 h-6 text-se-danger mx-auto mb-2" />
            <UiText as="p" variant="p" class="text-se-danger">
                An error occurred while searching
            </UiText>
        </div>

        <template v-else-if="!hasResults && !isFetchingValue">
            <Combobox.Empty>
                <slot name="empty">
                    <div class="px-4 py-8 text-center">
                        <UiText as="p" variant="p" class="text-se-gray-500">
                            No results found
                        </UiText>
                    </div>
                </slot>
            </Combobox.Empty>
        </template>

        <div v-else class="py-4 flex flex-col gap-4">
            <UiComboboxGroup
                v-for="group in groupedData"
                :key="group.id"
                :id="group.id"
                :label="group.label"
                :show-image="group.showImage"
            >
                <template #heading="{ label, id }" v-if="isGrouped">
                    <slot name="group-heading" :label="label" :id="id" :link="getLinkProps(group.link)">
                        <div class="flex items-center justify-between border-b border-se-gray-300 pb-1">
                            <UiText as="span" variant="h6" class="font-semibold">
                                {{ label }}
                            </UiText>
                            <UiLink
                                variant="h6"
                                v-if="getLinkProps(group.link).href"
                                :href="getLinkProps(group.link).href!"
                                :label="getLinkProps(group.link).label!"
                                class="hover:underline"
                            />
                        </div>
                    </slot>
                </template>

                <UiComboboxItem
                    v-for="item in getGroupItems(group.id)"
                    :key="item.value"
                    :item="item"
                    :value="item.value"
                    :label="item.label"
                    :image="item.image"
                    :rounded="item.rounded"
                    :initials="item.initials"
                    :show-image="item.showImage"
                    :showIndicator="multiple"
                />
            </UiComboboxGroup>
            <UiText v-if="props.hasMore" as="p" variant="caption-normal" class="px-4 text-center text-se-gray-500">
                {{ props.isFetchingValue ? 'Loading more…' : 'Scroll for more' }}
            </UiText>
        </div>
    </Combobox.Content>
</template>

